// src/pages/FundPlanner/components/Funds/index.jsx
import React, { useState, useCallback, useMemo } from "react";

import Page from "components/Page";
import TopTabMenu from "components/TopTabMenu";
import FileRibbon from "components/FileManager/FileRibbon";
import FileManagerPanel from "components/FileManager/FileManagerPanel";
import FileManagerModal from "components/FileManager/FileManagerModal";
import { DateInput } from "components/InputStyled";

import { useProfile } from "context/ProfileContext";
import { useDialog } from "context/DialogContext";
import { useNavigationGuard } from "hooks/useNavigationGuard";

import {
  useInvestmentFundsFileManager,
  investmentFundsOpenView,
} from "./useFileManager";

import Main from "./Main";
import EditPositions from "./FundPositions";
import CashFlows from "../Portfolios/CashFlows";
import { FundsProvider, useFunds } from "pages/FundPlanner/context/FundsContext";

function latestReportDateIso() {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  return date.toISOString().slice(0, 10);
}

function FundsInner() {
  const { availableOrgs, selectedOrg, orgMode, selectOrg } = useProfile();
  const { alert, confirm, showDialog } = useDialog();
  const fm = useInvestmentFundsFileManager();

  const {
    currentId,
    currentName,
    currentOwner,
    resetFund,
    loadFund,
    saveFund,
    saveAsFund,
    deleteFund,
    setCurrentName,
    isDirty,
    isSaving,
    canEdit,
    canDelete,
  } = useFunds();

  const [activeTab, setActiveTab] = useState("Main");
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [asOfDate, setAsOfDate] = useState(latestReportDateIso());
  const [cashFlowsExport, setCashFlowsExport] = useState({
    visible: false,
    disabled: true,
    label: "Export PDF",
    onClick: null,
  });

  const cashFlowScope = useMemo(
    () => ({
      reportMode: "single",
      asOfDate,
      includedPortfolioIds: [],
      includedFundIds: currentId ? [currentId] : [],
      includeAllPortfolioGroups: false,
    }),
    [asOfDate, currentId]
  );

  const showWorkspace = !!(currentId || isDirty);

  const handleSaveAs = useCallback(() => {
    saveAsFund({
      name: `${currentName || "New Fund"} Copy`,
    });
    setActiveTab("Main");
  }, [saveAsFund, currentName]);

  const handleSave = useCallback(async () => {
    const result = await saveFund();
    if (!result?.ok) {
      await alert(result?.error || "Unable to save the fund.", {
        title: "Unable to Save Fund",
      });
    }
    return result;
  }, [alert, saveFund]);

  const confirmUnsavedChanges = useCallback(
    async (message) => {
      if (!isDirty) return true;

      const buttons = canEdit
        ? [
            { label: "Save", value: "save", variant: "primary" },
            { label: "Discard", value: "discard", variant: "ghost" },
            { label: "Cancel", value: "cancel", variant: "brand-ghost" },
          ]
        : [
            { label: "Discard", value: "discard", variant: "primary" },
            { label: "Cancel", value: "cancel", variant: "ghost" },
          ];

      const result = await showDialog({
        title: "Unsaved Fund Changes",
        message: canEdit
          ? message ||
            "You have unsaved changes in this fund. Save before continuing?"
          : "You have unsaved changes in this fund, but you do not have permission to save them. Discard the changes and continue?",
        buttons,
        defaultAction: canEdit ? "save" : "cancel",
      });

      if (result === "save") {
        const saved = await handleSave();
        return !!saved?.ok;
      }

      return result === "discard";
    },
    [canEdit, handleSave, isDirty, showDialog]
  );

  const handleNew = useCallback(async () => {
    const canContinue = await confirmUnsavedChanges(
      "You have unsaved changes in this fund. Save before creating a new fund?"
    );
    if (!canContinue) return false;

    resetFund();
    setCurrentName("New Fund");
    setActiveTab("Main");
    return true;
  }, [confirmUnsavedChanges, resetFund, setCurrentName]);

  const handleOpen = useCallback(
    async (fund) => {
      if (String(fund?.id || "") === String(currentId || "")) {
        setShowOpenModal(false);
        return true;
      }

      const canContinue = await confirmUnsavedChanges(
        "You have unsaved changes in this fund. Save before opening another fund?"
      );
      if (!canContinue) return false;

      const loaded = await loadFund(fund.id);
      if (!loaded?.ok) {
        await alert(loaded?.error || "Unable to open the fund.", {
          title: "Unable to Open Fund",
        });
        return false;
      }

      setActiveTab("Main");
      fm.openRecord?.(fund);
      return true;
    },
    [alert, confirmUnsavedChanges, currentId, fm, loadFund]
  );

  const handleDelete = useCallback(async () => {
    if (!currentId) return;

    const confirmed = await confirm(
      `Delete fund "${currentName || "Untitled Fund"}"? This cannot be undone.`,
      {
        title: "Delete Fund",
        okLabel: "Delete",
        cancelLabel: "Cancel",
      }
    );

    if (!confirmed) return;

    const result = await deleteFund();

    if (!result?.ok) {
      await alert(result?.error || "Unable to delete the fund.", {
        title: "Unable to Delete Fund",
      });
      return;
    }

    await fm.refresh?.();
    setActiveTab("Main");
  }, [alert, confirm, currentId, currentName, deleteFund, fm]);

  const handleSelectOrg = useCallback(
    async (org) => {
      if (String(org?.id || "") === String(selectedOrg?.id || "")) return;

      const canContinue = await confirmUnsavedChanges(
        "You have unsaved changes in this fund. Save before switching organizations?"
      );
      if (!canContinue) return;

      selectOrg(org);
    },
    [confirmUnsavedChanges, selectedOrg?.id, selectOrg]
  );

  const discardForNavigation = useCallback(() => {
    resetFund();
  }, [resetFund]);

  useNavigationGuard(
    isDirty,
    handleSave,
    discardForNavigation,
    "You have unsaved changes in this fund. Save before leaving FundPlanner Funds?",
    { title: "Unsaved Fund Changes", canSave: canEdit }
  );

  const topTabs = [
    { key: "Main", label: "Main" },
    { key: "EditPositions", label: "Edit Positions" },
    { key: "CashFlows", label: "Cash Flows" },
  ];

  return (
    <Page
      title="FundPlanner - Funds"
      showOrganizationSelector
      onSelectOrganization={handleSelectOrg}
      ribbon={
        <FileRibbon
          entityName="Fund"
          fileManager={{
            availableOrgs,
            selectedOrg,
            orgMode,
            selectOrg: handleSelectOrg,
            newLabel: "New Fund",
          }}
          onNew={handleNew}
          onOpenBrowse={showWorkspace ? () => setShowOpenModal(true) : null}
          onSave={isDirty && canEdit ? handleSave : undefined}
          onSaveAs={showWorkspace ? handleSaveAs : undefined}
          onDelete={currentId && canDelete ? handleDelete : undefined}
          currentName={currentName || ""}
          currentOwner={currentOwner}
          animateSaving
          currentStatus={
            isSaving
              ? "saving"
              : currentId
                ? (isDirty ? "modified" : "saved")
                : "unsaved"
          }
        >
          {!showWorkspace ? (
            !selectedOrg ? (
              <div className="text-sm text-muted">
                Select an organization above to browse funds.
              </div>
            ) : (
              <FileManagerPanel
                entityName="Fund"
                adapter={fm}
                view={investmentFundsOpenView}
                onSelect={handleOpen}
                showToolbar
                showFooter
                maxHeightClassName="max-h-[55vh]"
              />
            )
          ) : null}
        </FileRibbon>
      }
    >
      {showWorkspace ? (
        <div className="rounded-2xl border border-border bg-app shadow-sm">
          <TopTabMenu
            tabs={topTabs}
            activeTab={activeTab}
            onChange={setActiveTab}
          />

          <div className="rounded-b-2xl border-t border-border p-6 space-y-6">
            {activeTab === "CashFlows" ? (
              <div className="flex flex-wrap items-end gap-4">
                <label className="flex flex-col">
                  <span className="mb-1 text-xs font-medium text-muted">Fund Name</span>
                  <input
                    value={currentName || "Untitled Fund"}
                    disabled
                    readOnly
                    className="h-9 min-w-[22rem] rounded-md border border-input bg-muted/20 px-3 text-sm text-foreground shadow-sm"
                  />
                </label>
                <DateInput
                  label="As of Date"
                  value={asOfDate}
                  onChange={setAsOfDate}
                  maxDate={latestReportDateIso()}
                  className="min-w-48"
                />
                {cashFlowsExport.visible ? (
                  <button
                    type="button"
                    className="btn btn-primary text-sm"
                    onClick={cashFlowsExport.onClick || undefined}
                    disabled={cashFlowsExport.disabled}
                    title="Export cash flows report to PDF"
                  >
                    {cashFlowsExport.label}
                  </button>
                ) : null}
              </div>
            ) : null}
            {activeTab === "Main" ? <Main /> : null}
            {activeTab === "EditPositions" ? <EditPositions /> : null}
            {activeTab === "CashFlows" ? (
              currentId ? (
                <CashFlows
                  reportScope={cashFlowScope}
                  scopeType="fund"
                  scopeName={currentName || ""}
                  showFundColumn={false}
                  onExportStateChange={setCashFlowsExport}
                />
              ) : (
                <div className="rounded-lg border border-border p-4 text-sm text-muted">
                  Save the fund before generating its cash-flow report.
                </div>
              )
            ) : null}
          </div>
        </div>
      ) : null}

      {showOpenModal && selectedOrg ? (
        <FileManagerModal
          entityName="Fund"
          adapter={fm}
          view={investmentFundsOpenView}
          onClose={() => setShowOpenModal(false)}
          onSelect={async (fund) => {
            const opened = await handleOpen(fund);
            if (opened) setShowOpenModal(false);
          }}
        />
      ) : null}
    </Page>
  );
}

export default function Funds() {
  return (
    <FundsProvider>
      <FundsInner />
    </FundsProvider>
  );
}
